import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}


// Alpha FAQ stability upgrade. The FAQ route can be served from Shopify's
// full-page cache with older native <details> markup, so upgrade that markup
// from a global asset that is present on the canonical page as well.
const alphaFaqStableUpgrade = () => {
  const faqList = document.querySelector('.alpha-faq__list');
  if (!faqList) return;

  // The current section markup already has its own stable button controller.
  // Only upgrade the older cached native-details version here.
  if (faqList.querySelector('.alpha-faq__summary')) return;

  const oldItems = faqList.querySelectorAll(':scope > details.alpha-faq__item');
  if (!oldItems.length) return;

  if (!document.getElementById('alpha-faq-stable-upgrade-styles')) {
    const style = document.createElement('style');
    style.id = 'alpha-faq-stable-upgrade-styles';
    style.textContent = `
      .alpha-faq .alpha-faq__stable-item {
        border: 1px solid #111111;
        border-radius: 10px;
        overflow: hidden;
        background: #111111;
        color: #ffffff;
        overflow-anchor: none;
      }
      .alpha-faq .alpha-faq__stable-summary {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        border: 0;
        margin: 0;
        cursor: pointer;
        text-align: left;
        padding: 20px 22px;
        font: inherit;
        font-weight: 650;
        color: #ffffff;
        background: #111111;
      }
      .alpha-faq .alpha-faq__stable-summary:hover,
      .alpha-faq .alpha-faq__stable-summary:focus-visible { background: #242424; }
      .alpha-faq .alpha-faq__stable-summary:focus-visible { outline: 2px solid #ffffff; outline-offset: -4px; }
      .alpha-faq .alpha-faq__stable-icon { flex: 0 0 auto; font-size: 1.4rem; line-height: 1; }
      .alpha-faq .alpha-faq__stable-item.is-open .alpha-faq__stable-icon { transform: rotate(45deg); }
      .alpha-faq .alpha-faq__stable-answer {
        padding: 0 22px 20px;
        line-height: 1.65;
        color: #f3f3f3;
        background: #111111;
      }
      .alpha-faq .alpha-faq__stable-answer p { margin: 0 0 12px; }
      .alpha-faq .alpha-faq__stable-answer p:last-child { margin-bottom: 0; }
      .alpha-faq .alpha-faq__stable-answer ul { margin: 0 0 12px; padding-left: 22px; }
      .alpha-faq .alpha-faq__stable-answer li { margin: 4px 0; }
      @media screen and (max-width: 749px) {
        .alpha-faq .alpha-faq__stable-summary { padding: 18px; }
        .alpha-faq .alpha-faq__stable-answer { padding-inline: 18px; }
      }
    `;
    document.head.appendChild(style);
  }

  oldItems.forEach((details) => {
    const oldSummary = details.querySelector(':scope > summary');
    const oldAnswer = details.querySelector(':scope > .alpha-faq__answer');
    if (!oldSummary || !oldAnswer) return;

    const item = document.createElement('div');
    item.className = 'alpha-faq__item alpha-faq__stable-item';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'alpha-faq__stable-summary';
    button.setAttribute('aria-expanded', 'false');

    const label = document.createElement('span');
    label.textContent = oldSummary.textContent.trim();
    const icon = document.createElement('span');
    icon.className = 'alpha-faq__stable-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '+';
    button.append(label, icon);

    const answer = document.createElement('div');
    answer.className = 'alpha-faq__stable-answer';
    answer.hidden = true;
    answer.innerHTML = oldAnswer.innerHTML;
    item.append(button, answer);
    details.replaceWith(item);
  });

  if (faqList.dataset.alphaFaqStableUpgrade === 'true') return;
  faqList.dataset.alphaFaqStableUpgrade = 'true';

  const restorePosition = (button, top) => {
    const offset = button.getBoundingClientRect().top - top;
    if (Math.abs(offset) > 0.25) window.scrollTo(window.scrollX, window.scrollY + offset);
  };

  faqList.addEventListener('click', (event) => {
    const button = event.target.closest('.alpha-faq__stable-summary');
    if (!button) return;
    const item = button.closest('.alpha-faq__stable-item');
    const answer = item?.querySelector('.alpha-faq__stable-answer');
    if (!item || !answer) return;

    const top = button.getBoundingClientRect().top;
    const opening = button.getAttribute('aria-expanded') !== 'true';
    const root = document.documentElement;
    const body = document.body;
    const oldRootAnchor = root.style.overflowAnchor;
    const oldBodyAnchor = body.style.overflowAnchor;
    root.style.overflowAnchor = 'none';
    body.style.overflowAnchor = 'none';

    button.setAttribute('aria-expanded', String(opening));
    item.classList.toggle('is-open', opening);
    answer.hidden = !opening;
    restorePosition(button, top);

    requestAnimationFrame(() => {
      restorePosition(button, top);
      requestAnimationFrame(() => {
        restorePosition(button, top);
        requestAnimationFrame(() => {
          restorePosition(button, top);
          root.style.overflowAnchor = oldRootAnchor;
          body.style.overflowAnchor = oldBodyAnchor;
        });
      });
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', alphaFaqStableUpgrade, { once: true });
} else {
  alphaFaqStableUpgrade();
}
